// Zuppin Vehicle Types Configuration
// Source of Truth - Only these 4 vehicle types are supported
// Capacity Priority: Zuppin API > Admin Override > Defaults below

export const VEHICLE_TYPES = {
  VAN: {
    id: 'VAN',
    name: 'VIP Van',
    image: 'https://res.cloudinary.com/dmu5qh4dj/image/upload/v1774334448/van_zxi1eg.png',
    maxPassengers: 8,
    maxLuggage: 6,
    description: 'Ideal for families',
    features: ['Air Conditioning', 'Free WiFi', 'Water Bottles', 'USB Charging'],
  },
  MINI: {
    id: 'MINI',
    name: 'VIP Mini Bus',
    image: 'https://res.cloudinary.com/dmu5qh4dj/image/upload/v1774334448/mini_r98z8m.png',
    maxPassengers: 16,
    maxLuggage: 13,
    description: 'Perfect for small groups',
    features: ['Air Conditioning', 'Free WiFi', 'Water Bottles', 'Extra Legroom'],
  },
  MIDI: {
    id: 'MIDI',
    name: 'VIP Midi Bus',
    image: 'https://res.cloudinary.com/dmu5qh4dj/image/upload/v1774334447/midi_pgk2do.png',
    maxPassengers: 27,
    maxLuggage: 20,
    description: 'Great for medium groups',
    features: ['Air Conditioning', 'Free WiFi', 'Water Bottles', 'Reclining Seats'],
  },
  BUS: {
    id: 'BUS',
    name: 'VIP Coach',
    image: 'https://res.cloudinary.com/dmu5qh4dj/image/upload/v1774334447/bus_esh2br.png',
    maxPassengers: 50,
    maxLuggage: 40,
    description: 'Best for large groups',
    features: ['Air Conditioning', 'Free WiFi', 'Water Bottles', 'Restroom', 'Entertainment System'],
  },
};

// Get vehicle type by ID
export const getVehicleType = (id) => {
  const type = VEHICLE_TYPES[id?.toUpperCase()];
  if (!type) {
    console.warn(`Unknown vehicle type: ${id}. Valid types are: VAN, MINI, MIDI, BUS`);
    return null;
  }
  return type;
};

// Get all vehicle types as array
export const getAllVehicleTypes = () => Object.values(VEHICLE_TYPES);

// Validate if vehicle type is supported
export const isValidVehicleType = (id) => {
  return Object.keys(VEHICLE_TYPES).includes(id?.toUpperCase());
};

/**
 * Filter vehicles by passenger AND luggage capacity
 * @param {number} passengerCount - Number of passengers
 * @param {number} luggageCount - Number of luggage items
 * @returns {Array} - Filtered vehicle types that can accommodate both
 */
export const filterVehiclesByCapacity = (passengerCount, luggageCount = null) => {
  const passengers = parseInt(passengerCount) || 1;
  // Default: luggage = passengers if not specified
  const luggage = luggageCount !== null ? parseInt(luggageCount) : passengers;
  
  return getAllVehicleTypes().filter(vehicle => 
    vehicle.maxPassengers >= passengers && vehicle.maxLuggage >= luggage
  );
};

/**
 * Check if any vehicle can accommodate the passenger AND luggage count
 * @param {number} passengerCount - Number of passengers
 * @param {number} luggageCount - Number of luggage items
 * @returns {boolean}
 */
export const hasAvailableVehicles = (passengerCount, luggageCount = null) => {
  const passengers = parseInt(passengerCount) || 1;
  const luggage = luggageCount !== null ? parseInt(luggageCount) : passengers;
  
  return getAllVehicleTypes().some(vehicle => 
    vehicle.maxPassengers >= passengers && vehicle.maxLuggage >= luggage
  );

};

/**
 * Check if a specific vehicle can accommodate passengers + luggage
 * @param {Object} vehicle - Vehicle object
 * @param {number} passengerCount - Number of passengers
 * @param {number} luggageCount - Number of luggage items
 * @returns {boolean}
 */
export const canVehicleAccommodate = (vehicle, passengerCount, luggageCount) => {
  const passengers = parseInt(passengerCount) || 1;
  const luggage = parseInt(luggageCount) || passengers;
  
  return vehicle.maxPassengers >= passengers && vehicle.maxLuggage >= luggage;
};

/**
 * Get the maximum passenger capacity across all vehicles
 * @returns {number}
 */
export const getMaxPassengerCapacity = () => {
  return Math.max(...getAllVehicleTypes().map(v => v.maxPassengers));
};

/**
 * Get the maximum luggage capacity across all vehicles
 * @returns {number}
 */
export const getMaxLuggageCapacity = () => {
  return Math.max(...getAllVehicleTypes().map(v => v.maxLuggage));
};

/**
 * Get suggested vehicle split for large groups
 * @param {number} passengerCount - Number of passengers
 * @param {number} luggageCount - Number of luggage items
 * @returns {Object|null} - Suggestion object or null if single vehicle works
 */
export const getSplitSuggestion = (passengerCount, luggageCount = null) => {
  const passengers = parseInt(passengerCount) || 1;
  const luggage = luggageCount !== null ? parseInt(luggageCount) : passengers;
  
  const maxPassengerCapacity = getMaxPassengerCapacity();
  const maxLuggageCapacity = getMaxLuggageCapacity();
  
  // Check if any single vehicle can handle both
  if (passengers <= maxPassengerCapacity && luggage <= maxLuggageCapacity) {
    // But maybe no single vehicle can handle the combination
    const hasMatch = hasAvailableVehicles(passengers, luggage);
    if (hasMatch) {
      return null; // No split needed
    }
  }
  
  // Find the best vehicle to split with
  const sortedVehicles = getAllVehicleTypes().sort((a, b) => b.maxPassengers - a.maxPassengers);
  const largestVehicle = sortedVehicles[0];
  
  const vehiclesNeededForPassengers = Math.ceil(passengers / largestVehicle.maxPassengers);
  const vehiclesNeededForLuggage = Math.ceil(luggage / largestVehicle.maxLuggage);
  const vehiclesNeeded = Math.max(vehiclesNeededForPassengers, vehiclesNeededForLuggage);
  
  return {
    vehiclesNeeded,
    vehicleType: largestVehicle,
    totalPassengerCapacity: vehiclesNeeded * largestVehicle.maxPassengers,
    totalLuggageCapacity: vehiclesNeeded * largestVehicle.maxLuggage,
    message: `Consider booking ${vehiclesNeeded} ${largestVehicle.name}${vehiclesNeeded > 1 ? 's' : ''} for your group of ${passengers} passengers and ${luggage} luggage items.`
  };
};
